import { BaseService } from './BaseService';
import { AccountService } from './AccountService';
import { FUND_CODES } from '../../utils/accountingCodes';
import { 
  Account,
  AccountType,
  AccountSpecialType,
  AccountCategory
} from '../types/accounting';
import { QueryResult } from 'pg';

interface AccountDefinition {
  code: string;
  name: string;
  subcategories?: AccountDefinition[];
}

interface ChartOfAccountsStructure {
  [category: string]: AccountDefinition[];
}

const CHART_OF_ACCOUNTS_STRUCTURE: ChartOfAccountsStructure = {
  // Your chart of accounts data...
};

// Define interfaces for PostgreSQL types to avoid direct imports
interface PgClientType {
  query: (text: string, params?: any[]) => Promise<any>;
  release: () => void;
}

interface PgPoolType {
  connect: () => Promise<PgClientType>;
  query: (text: string, params?: any[]) => Promise<any>;
}

// Create placeholder for PostgreSQL imports
let PgPool: { new(config: any): PgPoolType };

// Use dynamic import for PostgreSQL in Node.js environment
if (typeof window === 'undefined') {
  // Server-side code
  try {
    const pg = require('pg');
    PgPool = pg.Pool;
  } catch (e) {
    console.error('Failed to load pg:', e);
    // Provide fallback implementation
    PgPool = class {
      constructor(config: any) {}
      async connect() { 
        return { 
          query: async () => ({ rows: [] }), 
          release: () => {} 
        }; 
      }
      async query() { return { rows: [] }; }
    } as any;
  }
} else {
  // Client-side code - provide mock implementation
  PgPool = class {
    constructor(config: any) {}
    async connect() { 
      return { 
        query: async () => ({ rows: [] }), 
        release: () => {} 
      }; 
    }
    async query() { return { rows: [] }; }
  } as any;
}

export class ChartOfAccountsService extends BaseService {
  private accountService: AccountService;
  protected pool: any;

  constructor() {
    super();
    this.accountService = new AccountService();
    
    // Initialize the pool
    this.pool = new PgPool({
      host: process.env.DB_HOST || 'localhost',
      port: parseInt(process.env.DB_PORT || '5432'),
      database: process.env.DB_NAME || 'sponsfi',
      user: process.env.DB_USER || 'postgres',
      password: process.env.DB_PASSWORD || 'postgres'
    });
  }

  async initializeChartOfAccounts(fiscalSponsorId: string): Promise<void> {
    return await this.withTransaction(async (client) => {
      // Create root accounts first
      for (const [category, accounts] of Object.entries(CHART_OF_ACCOUNTS_STRUCTURE)) {
        for (const account of accounts as AccountDefinition[]) {
          const rootAccount = await this.accountService.createAccount(
            fiscalSponsorId,
            {
              code: account.code,
              name: account.name,
              type: this.determineAccountType(category),
              category: this.determineAccountCategory(category, account.name),
              description: `Root account for ${account.name}`,
              isContra: false
            }
          );

          // Create subcategories if they exist
          if (account.subcategories) {
            for (const subAccount of account.subcategories) {
              await this.accountService.createAccount(
                fiscalSponsorId,
                {
                  code: subAccount.code,
                  name: subAccount.name,
                  type: this.determineAccountType(category),
                  category: this.determineAccountCategory(category, account.name),
                  parent_id: rootAccount.id,
                  description: `Sub-account under ${account.name}`,
                  isContra: false
                }
              );
            }
          }
        }
      }

      // Create special fiscal sponsorship accounts
      await this.createFiscalSponsorshipAccounts(fiscalSponsorId);
    });
  }

  private async createFiscalSponsorshipAccounts(
    fiscalSponsorId: string
  ): Promise<void> {
    const sponsorshipAccounts = [
      {
        code: '2400',
        name: 'Fiscal Sponsorship Liabilities',
        type: AccountType.LIABILITY,
        category: {
          id: 'CURRENT_LIABILITIES',
          code: 'CL',
          name: 'Current Liabilities',
          type: AccountType.LIABILITY
        },
        subcategories: [
          {
            code: '2410',
            name: 'Sponsored Project Funds Payable'
          },
          {
            code: '2420',
            name: 'Fiscal Sponsorship Fees Payable'
          },
          {
            code: '2430',
            name: 'Inter-Project Transfers'
          }
        ]
      },
      {
        code: '4500',
        name: 'Fiscal Sponsorship Revenue',
        type: AccountType.REVENUE,
        category: {
          id: 'OTHER_REVENUE',
          code: 'OR',
          name: 'Other Revenue',
          type: AccountType.REVENUE
        },
        subcategories: [
          {
            code: '4510',
            name: 'Sponsorship Fee Revenue'
          },
          {
            code: '4520',
            name: 'Administrative Service Fees'
          },
          {
            code: '4530',
            name: 'Project Setup Fees'
          }
        ]
      },
      {
        code: '5300',
        name: 'Project Distributions',
        type: AccountType.EXPENSE,
        category: {
          id: 'PROGRAM_SERVICES',
          code: 'PS',
          name: 'Program Services',
          type: AccountType.EXPENSE
        },
        subcategories: [
          {
            code: '5310',
            name: 'Direct Project Expenses'
          },
          {
            code: '5320',
            name: 'Project Grant Distributions'
          },
          {
            code: '5330',
            name: 'Project Support Services'
          }
        ]
      }
    ];

    for (const account of sponsorshipAccounts) {
      const rootAccount = await this.accountService.createAccount(
        fiscalSponsorId,
        {
          code: account.code,
          name: account.name,
          type: account.type,
          category: account.category,
          description: `Fiscal sponsorship ${account.name.toLowerCase()}`,
          isContra: false
        }
      );

      for (const subAccount of account.subcategories) {
        await this.accountService.createAccount(
          fiscalSponsorId,
          {
            code: subAccount.code,
            name: subAccount.name,
            type: account.type,
            category: account.category,
            parent_id: rootAccount.id,
            description: `Sub-account for ${subAccount.name}`,
            isContra: false
          }
        );
      }
    }
  }

  private determineAccountType(category: string): AccountType {
    const categoryMap: Record<string, AccountType> = {
      'Assets': AccountType.ASSET,
      'Liabilities': AccountType.LIABILITY,
      'Net Assets': AccountType.EQUITY,
      'Revenue': AccountType.REVENUE,
      'Expenses': AccountType.EXPENSE
    };
    return categoryMap[category] || AccountType.ASSET;
  }

  private determineAccountCategory(category: string, name: string): AccountCategory {
    // Map the category string to an AccountCategory
    const categoryMap: Record<string, AccountCategory> = {
      'Assets': {
        id: 'ASSET',
        code: 'A',
        name: 'Assets',
        type: AccountType.ASSET,
      },
      'Liabilities': {
        id: 'LIAB',
        code: 'L',
        name: 'Liabilities',
        type: AccountType.LIABILITY,
      },
      'Net Assets': {
        id: 'EQUITY',
        code: 'E',
        name: 'Net Assets',
        type: AccountType.EQUITY,
      },
      'Revenue': {
        id: 'REV',
        code: 'R',
        name: 'Revenue',
        type: AccountType.REVENUE,
      },
      'Expenses': {
        id: 'EXP',
        code: 'X',
        name: 'Expenses',
        type: AccountType.EXPENSE,
      }
    };

    return categoryMap[category] || categoryMap['Assets']; // Default to Assets if category not found
  }

  async createFiscalSponsorAccount(accountData: Partial<Account>): Promise<Account> {
    return await this.withTransaction(async (client) => {
      // Validate and generate codes
      const accountCode = this.generateAccountCode(accountData);
      const taxCode = this.generateTaxCode(accountData);

      const query = `
        INSERT INTO sponsfi.accounts (
          fiscal_sponsor_id, code, name, type, category, 
          tax_category, is_restricted, fund_type, description,
          tax_code
        ) VALUES ($1, $2, $3, $4, $5, $6, $7, $8, $9, $10)
        RETURNING *
      `;

      const result = await this.query(query, [
        accountData.id,
        accountCode,
        accountData.name,
        accountData.type,
        accountData.category,
        accountData.taxCategory,
        accountData.isRestricted || false,
        accountData.fundType || null,
        accountData.description || null,
        taxCode
      ], client);

      return result.rows[0];
    });
  }

  private generateAccountCode(accountData: Partial<Account>): string {
    const prefix = accountData.type?.charAt(0) || 'A';
    const category = accountData.category?.replace(/[^A-Z0-9]/g, '') || '';
    const sequence = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `${prefix}${category}${sequence}`;
  }

  private generateTaxCode(accountData: Partial<Account>): string {
    const typeMap: Record<AccountType, string> = {
      [AccountType.ASSET]: 'AS',
      [AccountType.LIABILITY]: 'LI',
      [AccountType.EQUITY]: 'EQ',
      [AccountType.REVENUE]: 'RE',
      [AccountType.EXPENSE]: 'EX'
    };
    
    const prefix = typeMap[accountData.type || AccountType.ASSET];
    const category = accountData.category?.slice(0, 3).toUpperCase() || '';
    return `${prefix}${category}`;
  }

  protected async query(
    queryText: string,
    values: any[],
    client?: PgClientType
  ): Promise<QueryResult> {
    if (client) {
      return await client.query(queryText, values);
    }
    
    // Add null check for pool
    if (!this.pool) {
      throw new Error('Database pool not initialized');
    }
    
    return await this.pool.query(queryText, values);
  }
}